import React, { useState, useEffect } from 'react';
import './PriorApprovedForm.scss';
import classNames from 'classnames';
import InlineSVG from 'react-inlinesvg';
import infoIcon from '@images/icons/info-black.svg';
import { ByzzerModal } from '@/components/form';
import GreenDot from '@/images/icons/GreenDot.svg';
import YellowDot from '@/images/icons/YellowDot.svg';
import RedDot from '@/images/icons/RedDot.svg';
import { useTenantApi } from '@/hooks/useTenantApi';
import { ByzzerLink } from '@/components/form';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { TrackClick } from '@/analytics';

const MARKET_101_URL = 'https://learn.byzzer.ai/en/articles/6289646-markets-101-channels-geographies-accounts-and-more';

type PriorApprovalRetailer = {
    masterCompanyCode: string;
    masterCompanyName: string;
};

export function PriorApprovedForm() {
    const { getPriorApprovalStatus, getPriorApprovalRetailers } = useTenantApi();
    const [statusInfo, setStatusInfo] = useState(false);
    const [expireInfo, setExpireInfo] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([{}]);
    const [priorApprovalRetailers, setPriorApprovalRetailers] = useState<PriorApprovalRetailer[]>([]);

    useEffect(() => {
        renderPriorApprovalData();
    }, []);

    const mainClasses = classNames('prior-approved-form');

    const renderPriorApprovalData = async () => {
        setLoading(true);
        const approvalStatus = await getPriorApprovalStatus();
        if (approvalStatus) {
            setTableData(approvalStatus);
        }
        const priorApprovalRetailersList: PriorApprovalRetailer[] = await getPriorApprovalRetailers();
        setPriorApprovalRetailers(
            priorApprovalRetailersList?.length
                ? priorApprovalRetailersList?.sort((a, b) => {
                      return a?.masterCompanyName?.localeCompare(b?.masterCompanyName, 'en', { sensitivity: 'base' });
                  })
                : []
        );
        setLoading(false);
    };

    function approvalStatus(status) {
        switch (status) {
            case 'approved':
                return (
                    <span>
                        <img className={`${mainClasses}__request-status-table-icon-status`} src={GreenDot} alt={''} />
                        Approved
                    </span>
                );
            case 'pending':
                return (
                    <span>
                        <img className={`${mainClasses}__request-status-table-icon-status`} src={YellowDot} alt={''} />
                        Pending
                    </span>
                );
            case 'denied':
                return (
                    <span>
                        <img className={`${mainClasses}__request-status-table-icon-status`} src={RedDot} alt={''} />
                        Denied
                    </span>
                );
            default:
                return <span className={`${mainClasses}__request-status-table-null`}>--</span>;
        }
    }
    return (
        <div>
            <ByzzerMask loading={loading} />
            <div className={`${mainClasses}__section`}>
                <div className={`${mainClasses}__title`}>What is Prior Approval?</div>
                <div className={`${mainClasses}__subclass-subheader-content`}>
                    Prior Approval is when a retailer does not allow their data to be released to another company unless
                    they have directly approved it. The data for these retailers will still be included in channel-level
                    and geographical aggregates, but will not be available as a separate market to run data on unless
                    you get the required approval form the retailer. To learn more, read our article &nbsp;
                    <ByzzerLink>
                        <TrackClick name={`Prior Approval Info Market 101 clicked`}>
                            <a
                                href={MARKET_101_URL}
                                target="_blank"
                                className={`${mainClasses}__subclass-subheader-content-route`}
                                rel="noreferrer"
                            >
                                <span>Markets 101.</span>
                            </a>
                        </TrackClick>
                    </ByzzerLink>
                    <br />
                    <br />
                    Retailer approval is managed by NIQ. Our customer service team can help you get an approval request
                    started.
                </div>
            </div>
            <div className={`${mainClasses}__section`}>
                <div className={`${mainClasses}__title`}>Which retailers require Prior Approval?</div>
                <div className={`${mainClasses}__subclass-subheader-content`}>
                    In order to run reports or receive data for any of the below retailers, you will need to get Prior
                    Approval.
                    <div className={`${mainClasses}__subclass-subheader-content-list`}>
                        <div className={`${mainClasses}__subclass-subheader-content-list-section`}>
                            {priorApprovalRetailers?.map((retailer) => (
                                <div
                                    key={retailer?.masterCompanyCode}
                                    className={`${mainClasses}__subclass-subheader-content-list-item`}
                                >
                                    {retailer?.masterCompanyName}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${mainClasses}__section`}>
                <div className={`${mainClasses}__title`}>How to request Prior Approval</div>
                <div className={`${mainClasses}__subclass-subheader-content`}>
                    The process to request Prior Approval varies depending on the retailer. Reach out to your NIQ
                    account representative for instructions on how to proceed.
                </div>
            </div>
            <div className={`${mainClasses}__section`}>
                <div className={`${mainClasses}__title`}>Approved Markets</div>
                <div className={`${mainClasses}__request-status-content`}>
                    See which markets you have received approval for in your account. Reach out to Byzzer Support with
                    any questions.
                </div>
                <table className={`${mainClasses}__request-status-table`}>
                    <tbody>
                        <tr>
                            <th>Retailer</th>
                            <th>Approved Categories</th>
                        </tr>
                        {tableData?.length > 0 &&
                            tableData?.map((item: any, index) => (
                                <tr key={index}>
                                    {item.retailer ? (
                                        <td>{item.retailer}</td>
                                    ) : (
                                        <td className={`${mainClasses}__request-status-table-null`}>--</td>
                                    )}
                                    {item.categories?.length ? (
                                        <td>
                                            <ul>
                                                {item.categories.map((item, index) => (
                                                    <li key={index} className={`${mainClasses}__request-status-table-list`}>{item.category}</li>
                                                ))}
                                            </ul>
                                        </td>
                                    ) : item.retailer ? (
                                        <td>
                                            <ul>
                                                <li key={index} className={`${mainClasses}__request-status-table-list`}>ALL CATEGORIES</li>
                                            </ul>
                                        </td>
                                    ) : (
                                        <td className={`${mainClasses}__request-status-table-null`}>--</td>
                                    )}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <ByzzerModal
                show={statusInfo}
                onClose={() => setStatusInfo(!statusInfo)}
                size={'small'}
                headerType={'none'}
                className="byzzer-model-position"
            >
                <div className={`${mainClasses}__request-status-table-info`}>
                    <span className={`${mainClasses}__request-status-table-info-title`}>Pending:</span>
                    &nbsp;the request has been sent to NIQ and the retailer. Response from the retailer is pending.
                </div>
                <div className={`${mainClasses}__request-status-table-info`}>
                    <span className={`${mainClasses}__request-status-table-info-title`}>Approved:</span>
                    &nbsp; the retailer has approved your request, and their market should be available to select when
                    you run a report. If the retailer is also a premium market, reach out to customer service in order
                    to purchase.
                </div>
                <div className={`${mainClasses}__request-status-table-info`}>
                    <span className={`${mainClasses}__request-status-table-info-title`}>Denied:</span>
                    &nbsp;the retailer did not approve your request, so you cannot run reports on their markets.
                </div>
                <br />
                <div className={`${mainClasses}__request-status-table-info-note`}>
                    If a market you requested is not listed, it may not have been submitted by byzzer yet. Reach out to
                    customer service to follow up on the status of any requests.
                </div>
            </ByzzerModal>
            <ByzzerModal
                show={expireInfo}
                onClose={() => setExpireInfo(!expireInfo)}
                size={'small'}
                headerType={'none'}
                className="byzzer-model-position"
            >
                <div className={`${mainClasses}__request-status-table-info`}>
                    <span className={`${mainClasses}__request-status-table-info-title`}>Expiration Date:</span>
                    &nbsp; some Prior Approval retailers require re-approval on a cadence, such as annually. You will
                    need to submit another approval request to continue access beyond the expiration date.
                </div>
            </ByzzerModal>
        </div>
    );
}
export default PriorApprovedForm;
